import axios, { InternalAxiosRequestConfig } from "axios";
import { env } from "@/lib";
import { getAuthUser } from "@/lib/firebase";
import { getBuildingId } from "@/lib";

const apiClient = axios.create({
  baseURL: `${env("VITE_API_BASE")}/api`,
});
export const moreMemoryApiClient = axios.create({
  baseURL: `${env("VITE_API_BASE")}/api_more_memory`,
});

export const otherApiClient = axios.create({
  baseURL: `${env("VITE_API_BASE")}`,
});

const interceptor = async (config: InternalAxiosRequestConfig) => {
  const idToken = await getAuthUser().getIdToken();
  if (idToken) {
    config.headers.Authorization = `Bearer ${idToken}`;
  }
  // A request may name its building explicitly, e.g. to load a building before
  // switching to it; otherwise it's the building in the URL.
  config.headers.building ||= getBuildingId();
  return config;
};

/** Request config targeting `buildingId` instead of the building in the URL. */
export const forBuilding = (buildingId?: string) =>
  buildingId ? { headers: { building: buildingId } } : undefined;

apiClient.interceptors.request.use(interceptor, (error) => {
  return Promise.reject(error);
});

otherApiClient.interceptors.request.use(interceptor, (error) => {
  return Promise.reject(error);
});

moreMemoryApiClient.interceptors.request.use(interceptor, (error) => {
  return Promise.reject(error);
});

type ErrorResponseData = {
  code?: string;
  message?: string;
  [key: string]: unknown;
};

type CustomError = {
  response?: { status?: number; data?: ErrorResponseData };
};

/** An Error that still carries the HTTP status and body of the response it came from. */
export type StatusError = Error & { status?: number; data?: ErrorResponseData };

export const throwCaughtError = (error: unknown) => {
  const wrapped: StatusError = new Error("An unexpected error occurred: ", {
    cause: (error as CustomError)?.response?.data?.message || error,
  });
  // Wrapping loses the AxiosError, so carry the status across explicitly —
  // the retry policy in main.tsx needs it to tell a 4xx from a 5xx.
  wrapped.status = (error as CustomError)?.response?.status;
  wrapped.data = (error as CustomError)?.response?.data;
  throw wrapped;
};

export default apiClient;
